// modules
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "antd";

// service calls

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants and types
import { VOID_CR_TRANSACTION as Constants } from "constants/global";
import { FormInstance } from "antd/es/form/Form";
import { getCRTransaction, voidCRTransaction } from "services/api/crTransaction";

type VoidCRTransactionPropTypes = {
	proceedToGetCRTransaction: () => void;
	proceedToVoidCRTransaction: () => void;
	loading: boolean;
	posTransactionId: string;
	transactionDetails: any;
	hasVouchers: boolean;
	form: FormInstance;
	handlePosTransactionId: (e: { target: { id: any; value: any } }) => void;
};

export function useVoidCRTransactionPage(): VoidCRTransactionPropTypes {
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [loading, setLoading] = useState<boolean>(false);
	const [posTransactionId, setPosTransactionId] = useState<string>("");
	const [transactionDetails, setTransactionDetails] = useState<object>({ is_voided: true });
	const [hasVouchers, setHasVouchers] = useState<boolean>(false);

	const onSuccess = useCallback(() => {
		navigate(0);
	}, [navigate]);

	const onFailure = useCallback((error: any) => {
		setLoading(false);
		CountDownModal({
			closable: true,
			timer: 10,
			errorIcon: true,
			title: Constants.SERVER_ERR,
			subtitle: error?.message,
			onTimeComplete: () => {
				return false;
			}
		});
	}, []);

	const handlePosTransactionId = useCallback(
		(e: { target: { id: any; value: any } }) => {
			setPosTransactionId(e?.target?.value);
		},
		[posTransactionId]
	);

	const proceedToGetCRTransaction = useCallback(async () => {
		if (posTransactionId) {
			setLoading(true);
			await getCRTransaction({ pos_transaction_id: posTransactionId })
				.then((response) => {
					const data = response?.data?.data;
					if (!data) {
						throw new Error("Empty response");
					}

					// Ensure we're setting all the required fields correctly
					const details = {
						pos_transaction_id: data?.pos_transaction_id,
						is_voided: data?.is_voided,
						receipt_no: data?.pos_transaction_id?.split("-")?.[2] || "",
						date: data?.open_time,
						payments: data?.payments || []
					};

					setTransactionDetails(details);
					const hasVouchers = details && details.payments && details.payments.some((payment: any) => payment.type.toLowerCase().includes("voucher"));
					setHasVouchers(hasVouchers);
				})
				.catch((error) => {
					const errors = error?.response?.data?.errors;
					if (Array.isArray(errors) && errors.length > 0) {
						onFailure(errors[0]);
					} else {
						onFailure({ message: Constants.SERVER_ERR });
					}
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [posTransactionId, loading, setPosTransactionId]);

	const prepareVoidPayload = (transactionDetails: any) => {
		const posTransactionIdSegments = transactionDetails?.pos_transaction_id?.split("-") || [];
		const uid = transactionDetails?.payments && transactionDetails?.payments[0] ? transactionDetails?.payments[0]?.user_id : "unknown";

		return {
			outlet_code: posTransactionIdSegments[0] || "unknown",
			uid: uid,
			receipt_no: transactionDetails?.receipt_no || "",
			transaction_date_time_utc: transactionDetails?.date || ""
		};
	};

	const proceedToVoidCRTransaction = useCallback(async () => {
		setLoading(true);
		const voidPayload = prepareVoidPayload(transactionDetails);
		await voidCRTransaction(voidPayload.outlet_code, voidPayload.uid, voidPayload.receipt_no, voidPayload.transaction_date_time_utc)
			.then((response) => {
				CountDownModal({
					timer: 3,
					errorIcon: false,
					title: "Success",
					subtitle: response?.data?.message,
					onTimeComplete: onSuccess
				});
			})
			.catch((error) => {
				const errors = error?.response?.data?.errors;
				if (Array.isArray(errors) && errors.length > 0) {
					onFailure(errors[0]);
				} else {
					onFailure({ message: Constants.SERVER_ERR });
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [posTransactionId, loading, setPosTransactionId, transactionDetails, onSuccess, onFailure]);

	return {
		proceedToGetCRTransaction,
		proceedToVoidCRTransaction,
		loading,
		posTransactionId,
		transactionDetails: transactionDetails as { is_voided: boolean },
		form,
		handlePosTransactionId,
		hasVouchers
	};
}
